import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { LoaderKeys } from 'src/app/core/constants/constants';
import { AddBusinessOnboardingDetailsRequest, AddBusinessOnboardingDetailsResponse, AddBusinessOnboardingDetailsTempResponse, GetBusinessOnboardingDetailsRequest, GetBusinessOnboardingDetailsResponse, GetBusinessSegmentRequest, GetBusinessSegmentResponse, ScrapeBusinessOnboardingDetailsRequest, ScrapeBusinessOnboardingDetailsResponse, SkipScrapingRequest, UpdateBusinessOnboardingCompletionStatusRequest, UpdateBusinessOnboardingCompletionStatusResponse, UpdateBusinessOnboardingDetailsRequest, UpdateBusinessOnboardingDetailsResponse } from 'src/app/core/grpc-models/BusinessOnboarding_pb';
import { BusinessOnboardingProtoService } from 'src/app/core/grpc-services/BusinessOnboarding_pb_service';
import { GrpcService } from 'src/app/core/shared-services/grpc.service';
import { TokenService } from 'src/app/core/shared-services/token.service';

@Injectable({
  providedIn: 'root'
})
export class OnboardingService {

  isOnboardingBusinessDetailStepCompleted = false;
  isBusinessIntegrationTab = false;
  // isOnboardingBusinessIntegrationStepCompleted = false;
  isOnboardingCompleted?: boolean;
  businessCategoryName?: string;
  businessUsp?: string;

  businessOnboardingDetails = new GetBusinessOnboardingDetailsResponse().toObject();

  businessOnboardingDetailsSubject$ = new Subject<GetBusinessOnboardingDetailsResponse.AsObject>();
  businessOnboardingDetails$ = this.businessOnboardingDetailsSubject$.asObservable();

  private pendingOnboardingDetails: Promise<GetBusinessOnboardingDetailsResponse.AsObject> | null = null;
  private static readonly ONBOARDING_DETAILS_CACHE_MS = 3000;

  constructor(
    private readonly grpcService: GrpcService,
    private readonly tokenService: TokenService
  ) { }

  reset(): void {
    this.businessCategoryName = undefined;
    this.businessUsp = undefined;
  }

  public getBusinessOnboardingDetails(forceRefresh = false): Promise<GetBusinessOnboardingDetailsResponse.AsObject> {
    if (!forceRefresh && this.pendingOnboardingDetails) {
      return this.pendingOnboardingDetails;
    }

    const request = this.grpcService.grpcUnaryService(
      new GetBusinessOnboardingDetailsRequest(),
      BusinessOnboardingProtoService.GetBusinessOnboardingDetails,
      this.tokenService.getMetaData(), false
    ).then((response: GetBusinessOnboardingDetailsResponse.AsObject) => {
      // grpcUnaryService swallows errors into `undefined` — don't let a failed call
      // poison the cache for the next 3s of legitimate callers.
      if (!response) {
        this.pendingOnboardingDetails = null;
      }
      return response;
    });

    this.pendingOnboardingDetails = request;
    void request.finally(() => {
      setTimeout(() => {
        if (this.pendingOnboardingDetails === request) {
          this.pendingOnboardingDetails = null;
        }
      }, OnboardingService.ONBOARDING_DETAILS_CACHE_MS);
    });

    return request;
  }

  public getBusinessSegment(): Promise<GetBusinessSegmentResponse.AsObject> {
    return this.grpcService.grpcUnaryService(
      new GetBusinessSegmentRequest(),
      BusinessOnboardingProtoService.GetBusinessSegment,
      this.tokenService.getMetaData(), false
    );
  }

  public addBusinessOnboardingDetails(addOnboardingBusinessDetailsRequest: AddBusinessOnboardingDetailsRequest): Promise<AddBusinessOnboardingDetailsResponse.AsObject> {
    return this.grpcService.grpcUnaryService(
      addOnboardingBusinessDetailsRequest,
      BusinessOnboardingProtoService.AddBusinessOnboardingDetails,
      this.tokenService.getMetaData(), true, LoaderKeys.AddOnboardingBusinessDetailsKey
    );
  }

  public addBusinessOnboardingDetailsTemp(addOnboardingBusinessDetailsRequest: AddBusinessOnboardingDetailsRequest): Promise<AddBusinessOnboardingDetailsTempResponse.AsObject> {
    return this.grpcService.grpcUnaryService(
      addOnboardingBusinessDetailsRequest,
      BusinessOnboardingProtoService.AddBusinessOnboardingDetailsTemp,
      undefined, true, LoaderKeys.AddOnboardingBusinessDetailsKey
    );
  }

  public updateBusinessOnboardingDetails(updateOnboardingBusinessDetailsRequest: UpdateBusinessOnboardingDetailsRequest): Promise<UpdateBusinessOnboardingDetailsResponse.AsObject> {
    return this.grpcService.grpcUnaryService(
      updateOnboardingBusinessDetailsRequest,
      BusinessOnboardingProtoService.UpdateBusinessOnboardingDetails,
      this.tokenService.getMetaData(), true, LoaderKeys.UpdateOnboardingBusinessDetailsKey
    );
  }

  public updateOnboardingCompletionStatus(updateOnboardingCompletionStatusRequest: UpdateBusinessOnboardingCompletionStatusRequest): Promise<UpdateBusinessOnboardingCompletionStatusResponse.AsObject> {
    return this.grpcService.grpcUnaryService(
      updateOnboardingCompletionStatusRequest,
      BusinessOnboardingProtoService.UpdateBusinessOnboardingCompletionStatus,
      this.tokenService.getMetaData(), false, LoaderKeys.UpdateOnboardingCompletionStatusKey
    );
  }

  public scrapeBusinessOnboardingDetails(request: ScrapeBusinessOnboardingDetailsRequest): Promise<ScrapeBusinessOnboardingDetailsResponse.AsObject> {
    return this.grpcService.grpcUnaryService(
      request,
      BusinessOnboardingProtoService.ScrapeBusinessOnboardingDetails,
      this.tokenService.getMetaData(), false, LoaderKeys.ScrapeOnboardingBusinessDetailsKey
    );
  }

  public skipScraping(): Promise<ScrapeBusinessOnboardingDetailsResponse.AsObject> {
    return this.grpcService.grpcUnaryService(
      new SkipScrapingRequest(),
      BusinessOnboardingProtoService.SkipScraping,
      this.tokenService.getMetaData(), false
    );
  }


  public scrapeAndUpdateBusinessOnboardingDetails(request: UpdateBusinessOnboardingDetailsRequest): Promise<UpdateBusinessOnboardingDetailsResponse.AsObject> {
    return this.grpcService.grpcUnaryService(
      request,
      BusinessOnboardingProtoService.ScrapeAndUpdateBusinessOnboardingDetails,
      this.tokenService.getMetaData(), false, LoaderKeys.ScrapeOnboardingBusinessDetailsKey
    );
  }

  public scrapeBusinessOnboardingDetailsTemp(request: ScrapeBusinessOnboardingDetailsRequest): Promise<ScrapeBusinessOnboardingDetailsResponse.AsObject> {
    return this.grpcService.grpcUnaryService(
      request,
      BusinessOnboardingProtoService.ScrapeBusinessOnboardingDetailsTemp,
      undefined, false, LoaderKeys.ScrapeOnboardingBusinessDetailsKey
    );
  }

}

